import { AssessmentScenarioData } from '../types/assessments';
import { ListForcePackageData, Optional, RadioProfileRef } from '../types/common';
import { ForcePackageData } from '../types/force-packages';
import { EmitterData, PathlossCatalogResponse, RadioParameters } from '../types/systems';
import {
	isRadioParameterInRange,
	RADIO_BUDGET_FIELDS,
	RADIO_PARAMETER_LIMITS,
} from './radioParameters';

export { RADIO_BUDGET_FIELDS } from './radioParameters';

const isFiniteNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

export const validateRadioParameters = (radio?: RadioParameters): string[] => {
	if (!radio) return [];
	const errors: string[] = [];
	if (!radio.profileId?.trim()) errors.push('Select a propagation profile.');
	const missingBudget = RADIO_BUDGET_FIELDS.filter((field) => !isFiniteNumber(radio[field]));
	if (missingBudget.length > 0)
		errors.push(`Complete the radio budget: ${missingBudget.join(', ')}.`);
	for (const field of RADIO_BUDGET_FIELDS) {
		const value = radio[field];
		if (isFiniteNumber(value) && !isRadioParameterInRange(field, value)) {
			const { label, min, max, unit } = RADIO_PARAMETER_LIMITS[field];
			errors.push(`${label} must be between ${min} and ${max} ${unit}.`);
		}
	}

	const thresholds = radio.linkQualityDisplayThresholdsDb;
	if (
		!Array.isArray(thresholds) ||
		thresholds.length !== 3 ||
		!thresholds.every(isFiniteNumber) ||
		thresholds[0] !== 0 ||
		thresholds[1] <= 0 ||
		thresholds[2] <= thresholds[1] ||
		thresholds[2] > 100
	) {
		errors.push('Display thresholds must be three increasing values from 0 to 100 dB.');
	}
	return errors;
};

const refLabel = (ref: RadioProfileRef) => {
	const agent = ref.agent_id === undefined ? '' : `agent ${ref.agent_id}, `;
	return `${agent}${ref.system_name || `system ${ref.system_id}`}, ${ref.emitter_name}`;
};

const activeRefs = (refs: RadioProfileRef[]) => refs.filter((ref) => ref.radio_parameters_present);

const catalogWarnings = (refs: RadioProfileRef[], catalog: PathlossCatalogResponse): string[] => {
	const active = activeRefs(refs);
	if (active.length === 0) return [];
	const warnings: string[] = [];

	for (const ref of active) {
		if (ref.missing_budget_fields.length > 0) {
			warnings.push(
				`${refLabel(ref)} has incomplete radio parameters: ${ref.missing_budget_fields.join(', ')}.`
			);
		}
	}

	if (!catalog.available) {
		warnings.push(
			`Pathloss catalog is unavailable; profile maps cannot be verified. ${catalog.reason || ''}`.trim()
		);
		return warnings;
	}

	const knownIds = new Set(catalog.profiles.map((profile) => profile.profileId));
	for (const ref of active) {
		if (ref.profile_id && !knownIds.has(ref.profile_id)) {
			warnings.push(
				`${refLabel(ref)} uses profile '${ref.profile_id}', which is not in the pathloss catalog.`
			);
		}
	}
	return warnings;
};

export const collectDraftForcePackageRefs = (
	settings: Optional<ForcePackageData, 'id'>
): RadioProfileRef[] => {
	const refs: RadioProfileRef[] = [];
	const visit = (echelons: ForcePackageData['fp']['echelons']) => {
		for (const echelon of echelons) {
			for (const instance of echelon.systems) {
				const system = settings.systems[instance.system_id];
				for (const ref of system?.radio_profile_refs || []) {
					refs.push({
						...ref,
						agent_id: instance.agent_id,
						system_id: instance.system_id,
						system_name: system.name,
					});
				}
			}
			visit(echelon.echelons);
		}
	};
	visit(settings.fp.echelons);
	return refs;
};

export const getForcePackagePathlossWarnings = (
	settings: Optional<ForcePackageData, 'id'>,
	catalog: PathlossCatalogResponse
): string[] => {
	const refs = collectDraftForcePackageRefs(settings);
	const warnings = catalogWarnings(refs, catalog);
	if (!catalog.available) return warnings;

	const profiles = new Map(catalog.profiles.map((profile) => [profile.profileId, profile]));
	const maps = new Map<string, string[]>();
	for (const ref of activeRefs(refs)) {
		const profile = ref.profile_id ? profiles.get(ref.profile_id) : undefined;
		if (!profile) continue;
		maps.set(profile.scenario, [...(maps.get(profile.scenario) || []), refLabel(ref)]);
	}
	if (maps.size > 1) {
		warnings.push(
			`This force package mixes pathloss maps: ${Array.from(maps.entries())
				.map(([map, labels]) => `${map} (${labels.join('; ')})`)
				.join(' and ')}.`
		);
	}
	return Array.from(new Set(warnings));
};

export const getScenarioPathlossWarnings = (
	settings: Optional<AssessmentScenarioData, 'id'>,
	forcePackages: ListForcePackageData[],
	catalog: PathlossCatalogResponse
): string[] => {
	const selected = forcePackages.filter(
		(fp) => fp.id === settings.blue_force_package || fp.id === settings.red_force_package
	);
	const refs = selected.flatMap((fp) => fp.radio_profile_refs || []);
	const warnings = catalogWarnings(refs, catalog);
	if (!catalog.available) return Array.from(new Set(warnings));

	const expectedMap = `${settings.geography}_${settings.resolution}`.toLocaleLowerCase();
	const profiles = new Map(catalog.profiles.map((profile) => [profile.profileId, profile]));
	for (const ref of activeRefs(refs)) {
		const profile = ref.profile_id ? profiles.get(ref.profile_id) : undefined;
		if (profile && profile.scenario.toLocaleLowerCase() !== expectedMap) {
			warnings.push(
				`${refLabel(ref)} uses map '${profile.scenario}', but the scenario uses '${settings.geography}_${settings.resolution}'.`
			);
		}
	}
	return Array.from(new Set(warnings));
};

export const getEmitterPathlossWarnings = (
	settings: Optional<EmitterData, 'id'>,
	catalog: PathlossCatalogResponse
): string[] => {
	if (!settings.radio_parameters) return [];
	if (!catalog.available) {
		return [
			`Pathloss catalog is unavailable; the selected profile cannot be verified. ${catalog.reason || ''}`.trim(),
		];
	}
	if (
		settings.radio_parameters.profileId &&
		!catalog.profiles.some(
			(profile) => profile.profileId === settings.radio_parameters?.profileId
		)
	) {
		return [`Profile '${settings.radio_parameters.profileId}' is not in the pathloss catalog.`];
	}
	return [];
};
